// Text for the Budget Tracker (TAV-3340, TAV-3442, TAV-3459, TAV-3482). The single source for every title, description, label,
// tooltip, banner and note on the tracker; the Confluence pages mirror this text, so change both together. Term
// definitions shared with the SaH Dashboard come from BUDGET_TERMS.
import { BUDGET_TERMS } from '~support-at-home/lib/budgetTerms';

export const TRACKER_TEXT = {
  summary: {
    section:
      "This budget's figures over its dates, in two steps. Allocation compares the agreed budget with the client's " +
      'funding; Utilisation shows how much of the agreed budget is booked and claimed. Each underlined figure name ' +
      'explains itself on hover or keyboard focus.',
    available:
      "The total funding for this client over the budget's dates, stated as one figure. It includes " +
      `${BUDGET_TERMS.available.includes}. If Booked plus Claimed is more than Available, the banner says so and ` +
      'the figure over it is marked Over funding.',
    allocated: `The total of every line in this budget: what has been agreed with the client. ${
      BUDGET_TERMS.allocated.lineValue
    }`,
    allocationRate:
      `${BUDGET_TERMS.allocationRate.formula}: how much of the client's funding this budget commits. Above 100% ` +
      'means the budget commits more than the client has.',
  },
  // Tile lines (the one line under each figure) are kept to 2-4 words; the tooltips hold the full explanations.
  allocation: {
    title: 'Allocation',
    subtitle: 'Available vs budgeted',
    availableLine: 'Funding for the period',
    allocatedLine: 'In the agreed budget',
    residualLine: BUDGET_TERMS.unallocated.formula,
    rateLine: BUDGET_TERMS.allocationRate.formula,
    residual:
      `${BUDGET_TERMS.unallocated.formula}: funding the agreed budget leaves unused. Below zero the agreed budget ` +
      'is over the funding: it commits more than the client has.',
    careManagementIncluded: "This budget's total was entered including care management.",
    careManagementAdded: 'Care management was added to the total entered for this budget.',
  },
  utilisation: {
    title: 'Utilisation',
    subtitle: 'Booked vs budgeted',
    allocatedLine: 'In the agreed budget',
    bookedLine: 'Not yet claimed',
    claimedLine: 'Submitted or finalised claims',
    remainingLine: 'Left to utilise',
    rateLine: BUDGET_TERMS.utilisationRate.formula,
    rate:
      `${BUDGET_TERMS.utilisationRate.formula}: how much of the agreed budget is booked and claimed. Above 100% means more ` +
      'is booked and claimed than the budget agreed.',
  },
  pills: { overFunding: 'Over funding', overBudget: 'Over budget' },
  banner: {
    // Always this heading, then one line per item
    title: (count: number) => `${count} ${count === 1 ? 'item is' : 'items are'} over a limit`,
    booked: 'Booked',
    claimed: 'Claimed',
    bookedAndClaimed: 'Booked / claimed',
    overFunding: (who: string, amount: string, available: string) =>
      `${who} is ${amount} over the available funding (${available})`,
    agreedBudgetOverFunding: (amount: string, available: string) =>
      `The agreed budget is ${amount} over the available funding (${available}). ${BUDGET_TERMS.prompts.updateBudget}`,
    overAgreedBudget: (who: string, amount: string, allocated: string) =>
      `${who} is ${amount} over the agreed budget (${allocated}). ${BUDGET_TERMS.prompts.updateBudget}`,
    overAllowance: (who: string, amount: string, allowance: string) =>
      `${who} care management is ${amount} over the allowance (${allowance})`,
    overCategory: (who: string, category: string, amount: string, allocated: string) =>
      `${who} in ${category} is ${amount} over its allocation (${allocated}). ${BUDGET_TERMS.prompts.updateBudget}`,
  },
  overview: {
    section:
      'How the agreed budget (Allocated) is used. The ring fills up to Allocated; if Booked and Claimed together come ' +
      'to more than Allocated, the amount beyond it runs on as a second, amber ring outside the first.',
    centreLabel: 'Allocated',
    booked:
      "Appointments and approved supplier invoices in this budget's dates that are not yet in a finalised claim. " +
      `${BUDGET_TERMS.booked.occurrences} ${BUDGET_TERMS.booked.cancellations}`,
    claimed:
      'Appointments and supplier invoices in a submitted or finalised claim, ' +
      `${BUDGET_TERMS.claimed.value}. ${BUDGET_TERMS.claimed.ignored}`,
    remaining:
      `${BUDGET_TERMS.remaining.formula}: care agreed in the budget that is not booked yet. Below zero means more ` +
      'is booked or claimed than the budget agreed.',
    notYetBookedLabel: 'Not yet booked',
    notYetBooked: 'Allocated not yet booked or claimed. Zero once the agreed budget is used up.',
    // Added to the Booked and Claimed legend tooltips: the legend shows only the part within Allocated (TAV-3482)
    withinAllocated: 'Shown here up to Allocated, claimed counting first; any more is under Over the agreed budget.',
    overLabel: 'Over the agreed budget',
    // The legend row under the ring, only when over, with its booked and claimed parts (TAV-3482)
    over:
      'Booked and Claimed together beyond Allocated, claimed counting against Allocated first. It is the amber ' +
      'second ring outside the first.',
    bookedOverLabel: 'Booked',
    claimedOverLabel: 'Claimed',
    // The ring's accessible name, when work is beyond Allocated
    overAria: (amount: string) => `over the agreed budget ${amount}`,
  },
  careManagement: {
    title: 'Care Management Overview',
    subtitle: 'Care management used against the allowance',
    section:
      'Care management used against the allowance. The ring fills up to the allowance; anything used beyond it runs ' +
      'on as a second, amber ring outside the first.',
    centreLabel: 'Allowance',
    // The figure names, shared by the ring's segments and the keyed figures beside it
    legend: {
      allowance: 'Allowance',
      used: 'Used',
      unused: 'Unused',
      over: 'Over the allowance',
    },
    noCareManagement: 'N/A',
    noCareManagementLabel: 'This budget has no care management',
    allowance: BUDGET_TERMS.careManagement.allowance,
    used: (singles: number, repeats: number) =>
      `${BUDGET_TERMS.careManagement.used} Counted here: ` +
      `${singles} single ${singles === 1 ? 'appointment' : 'appointments'} and ${repeats} repeat ${repeats === 1 ? 'occurrence' : 'occurrences'}.`,
    unused: 'Allowance not yet used. Zero once the allowance is used up.',
    over:
      'Care management used beyond the allowance, claimed counting against the allowance first. It is the amber ' +
      'second ring outside the first.',
    // The ring's accessible name, when care management is used beyond the allowance
    overAria: (amount: string) => `over the allowance ${amount}`,
    remaining:
      'Allowance minus Used: care management still available. Below zero means more care management has been ' +
      'booked or claimed than the allowance.',
  },
  breakdown: {
    title: 'Budget Breakdown',
    description: 'The allocated budget by category and service. All bars use the same scale.',
    section:
      'An appointment counts in the category its service sits in on the day it happens. Personal care before 1 ' +
      'October 2026 is Independence; from 1 October it is Clinical Support. Each service under each category it ' +
      'sits in. A service with lines in two categories appears under both, with ' +
      "the work dated in each category's period.",
    tableCaption: 'Allocated, Booked, Claimed and Remaining by category and service',
    expandAll: 'Expand all',
    collapseAll: 'Collapse all',
    totalName: 'Total',
    toggle: {
      showServices: (name: string) => `Show services in ${name}`,
      hideServices: (name: string) => `Hide services in ${name}`,
    },
    columns: {
      name: 'Category / service',
      bar: 'Inside Allocated',
      allocated: 'Allocated',
      booked: 'Booked',
      claimed: 'Claimed',
      remaining: 'Remaining',
      utilisation: 'Utilisation',
    },
    legend: {
      booked: 'Booked',
      claimed: 'Claimed',
      remaining: 'Remaining',
      bookedOverAllocated: 'Over allocation',
    },
    bar:
      "Booked, then Claimed, then Remaining, as parts of the row's Allocated. Amber is Booked and Claimed over " +
      'Allocated. Every bar uses the same scale, so lengths compare across rows.',
    allocated: "The total of the row's budget lines: what the budget agreed for this category or service.",
    booked:
      'As Booked in Budget Overview, for this row only: booked work whose service sits in this category on the day ' +
      'it happens.',
    claimed:
      'As Claimed in Budget Overview, for this row only: claimed work whose service sits in this category on the ' +
      'day it happens.',
    remaining:
      `${BUDGET_TERMS.remaining.formula} for this row: care agreed that is not booked yet. Below zero means more ` +
      'is booked or claimed than allocated.',
    utilisation:
      `${BUDGET_TERMS.utilisationRate.formula}, for this row. Above 100% means more is booked and claimed than the ` +
      'row allocated.',
  },
  toolbar: { openBudget: 'Open budget' },
} as const;
